import { getConnectApi } from '@squareup/dex-data-shared-connect-api';
import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import { ApplicationType } from '@squareup/dex-types-shared-application-type';
import { SandboxAuthorization } from '@squareup/dex-types-shared-sandbox-test-account';
import { publishUnhandledError } from '@squareup/dex-utils-application-behavior-events';
import { useCallback } from 'react';

const useRenewAuthorizationToken = () => {
  const [getOauthClients] =
    getDeveloperApi().useLazyGetOauthClientsQuery(undefined);

  const [obtainTokenMutation] = getConnectApi().useObtainOAuthTokenMutation();

  const [updateSandboxAuthorization] =
    getDeveloperApi().useUpdateSandboxAuthorizationMutation();

  const renewToken = useCallback(
    async (sandboxAuthorization: SandboxAuthorization) => {
      try {
        if (!sandboxAuthorization.refresh_token) {
          throw new Error('No refresh token for sandbox authorization');
        }

        const oauthClients = await getOauthClients(
          ApplicationType.Sandbox,
          undefined
        ).unwrap();

        const productionClient = oauthClients?.['oauth-clients'].find(
          (oauthClient) => oauthClient.id === sandboxAuthorization.client
        );

        // Really shouldn't happen, but on the chance we somehow don't have a client
        // then throw since we can't revoke the token
        if (!productionClient) {
          throw new Error('Unable to find client for sandbox authorization');
        }

        // We renew a sandbox authorization token by:
        //   1. renewing the underlying oauth token
        //   2. updating the sandbox authorization with the new access token & date
        const refreshedToken = await obtainTokenMutation({
          accessToken: {
            client_id: sandboxAuthorization.client,
            refresh_token: sandboxAuthorization.refresh_token,
          },
          clientSecret: productionClient.secret,
        }).unwrap();

        const updatedSandboxAuthorization = {
          ...sandboxAuthorization,
          access_token: refreshedToken.access_token,
          expires_at: refreshedToken.expires_at,
        };

        await updateSandboxAuthorization(updatedSandboxAuthorization).unwrap();
      } catch (error) {
        publishUnhandledError(error as Error);
        throw error;
      }
    },
    [getOauthClients, obtainTokenMutation, updateSandboxAuthorization]
  );

  return {
    renewToken,
  };
};

export { useRenewAuthorizationToken };
